import GoogleMapReact from "google-map-react";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import styled from "@emotion/styled";
import log from "loglevel";
import {Constants, TenantPreferences, UserPreferences} from "../../constants/constants";
import {Job, RoutePlanningRoute, useGetRouteWithEtasLazyQuery} from "../../generated/graphql";
import {RoutePlanningDriver, useRoutePlanningContext} from "../../providers/RoutePlanningProvider";
import {
  extractJobAndJobStopIdsFromActivity,
  toVehicleId,
  findDriverByVehicleId,
  getStopActivities
} from "./utils/RoutePlanning.utils";
import {buildRouteWithEtasVariables, parseRouteWithEtasResult, RouteEtasUpdate} from "./GetRouteWithEtas.service";
import {RowData} from "./types/types";
import {StopInfoMetadata} from "../map/stop/StopInfo";
import {EntityVisibilityLevel} from "../map/MapVisibilityContext";
import MapReframe from "../map/action/MapReframe";
import RoutePlanningActionBar from "./RoutePlanningActionBar";
import {StopPin} from "./map/StopPin";
import {DriverPin} from "./map/DriverPin";
import {ClusteredStopsPin} from "./map/ClusteredStopsPin";
import {ClusteredDriverPins} from "./map/ClusteredDriverPins";
import VehicleIconResolver from "../vehicles/VehicleIconResolver";
import {extractJsonPref, extractSimplePref, usePreferenceContext} from "../../providers/PreferenceProvider";
import {getDefaultCardConfigurations} from "../manifest/card-configuration/v2/constants/constants";
import {IManifestCardConfigurationsV2} from "../manifest/card-configuration/v2/types/manifest-card-configurations.type";
import {DriverInformationValue} from "../manifest/card-configuration/v2/constants/enums";

type PolylineEntry = {
  routeId: string;
  driverId: number;
  polyline: google.maps.Polyline;
};

type Props = {
  routes: RoutePlanningRoute[];
  selectedJobs: Job[];
  selectedDrivers: RoutePlanningDriver[];
  hiddenRouteIds: string[];
  workingRowData?: RowData[] | null;
  onToggleAll: () => void;
  onRouteEtasUpdate?: (routeId: string, update: RouteEtasUpdate) => void;
  onRecalcLoadingChange?: (loading: boolean) => void;
  dwellSeconds?: number;
  isTrafficActive: boolean;
  onTrafficToggle: () => void;
  mapType: string;
  onMapTypeChange: (type: string) => void;
  newWindow?: Window;
  usePortal?: boolean;
  isPortalOpen?: boolean;
  onPopOut?: () => void;
};

const RoutesResultMap = React.memo(
  ({
    routes,
    selectedJobs,
    selectedDrivers,
    hiddenRouteIds,
    workingRowData,
    onToggleAll,
    onRouteEtasUpdate,
    onRecalcLoadingChange,
    dwellSeconds,
    isTrafficActive,
    onTrafficToggle,
    mapType,
    onMapTypeChange,
    newWindow,
    usePortal,
    isPortalOpen,
    onPopOut
  }: Props) => {
    const {tenantPreferences, userPreferences} = usePreferenceContext();

    const isUseETAService = extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;

    const cardConfigurationsV2: IManifestCardConfigurationsV2 = useMemo(
      () =>
        extractJsonPref(
          userPreferences,
          UserPreferences.manifestCardConfigurations,
          getDefaultCardConfigurations(isUseETAService)
        ).value,
      [userPreferences, isUseETAService]
    );

    const isShowCode = useMemo(
      () => !cardConfigurationsV2.driverInformation?.includes(DriverInformationValue.DriverName),
      [cardConfigurationsV2.driverInformation]
    );
    const [map, setMap] = useState<any>(null);
    const [maps, setMaps] = useState<any>(null);
    const polylinesRef = useRef<PolylineEntry[]>([]);
    const initialBoundsFittedRef = useRef(false);
    const [getRouteWithEtas] = useGetRouteWithEtasLazyQuery();
    const getRouteWithEtasRef = useRef(getRouteWithEtas);
    getRouteWithEtasRef.current = getRouteWithEtas;
    const onRouteEtasUpdateRef = useRef(onRouteEtasUpdate);
    onRouteEtasUpdateRef.current = onRouteEtasUpdate;
    const onRecalcLoadingChangeRef = useRef(onRecalcLoadingChange);
    onRecalcLoadingChangeRef.current = onRecalcLoadingChange;
    const dwellSecondsRef = useRef(dwellSeconds);
    dwellSecondsRef.current = dwellSeconds;
    const hiddenRouteIdsRef = useRef(hiddenRouteIds);
    hiddenRouteIdsRef.current = hiddenRouteIds;
    const [mapContainerWidth, setMapContainerWidth] = useState(0);
    const [showDriverReturnPath] = useState(true);

    const trafficLayerRef = useRef<any>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);

    const apiIsLoaded = useCallback(({map: mapObj, maps: mapsObj}: {map: any; maps: any}) => {
      setMap(mapObj);
      setMaps(mapsObj);
    }, []);

    // Track container width for responsive action bar
    useEffect(() => {
      if (!mapContainerRef.current) return;
      const ro = new ResizeObserver(([entry]) => setMapContainerWidth(entry.contentRect.width));
      ro.observe(mapContainerRef.current);
      return () => ro.disconnect();
    }, []);

    // Build a coordinate lookup from GH activities + pre-geocoded manifest stops
    const {originalResult: routePlanningResult} = useRoutePlanningContext();
    const stopCoordinates = useMemo(() => {
      const coords = new Map<number, {lat: number; lon: number}>();
      // GH activity coordinates
      routes.forEach((route) => {
        getStopActivities(route.activities).forEach((activity) => {
          const lat = activity.address?.lat;
          const lon = activity.address?.lon;
          if (lat == null || lon == null || !activity.location_id) return;
          const [, jobStopId] = extractJobAndJobStopIdsFromActivity(activity);
          if (jobStopId) coords.set(jobStopId, {lat, lon});
        });
      });
      // DP-3219: Override with the coordinates geocodeManifestStops resolved. GH activity
      // coords may differ from the central geocode cache for the same address; stopCoords
      // reads the cache (GH priority + frontend geocode fill) → override so every stop at
      // one address shares a coordinate and clusters properly.
      const preGeocoded = routePlanningResult?.stopCoords;
      if (preGeocoded) {
        for (const [idStr, coord] of Object.entries(preGeocoded)) {
          coords.set(Number(idStr), coord);
        }
      }
      return coords;
    }, [routes, routePlanningResult?.stopCoords]);

    // Per-route signatures keyed by routeId (stable across driver changes)
    const routeSignatureMap = useMemo(() => {
      if (!workingRowData) return null;
      const sigMap = new Map<string, string>();
      workingRowData.forEach((row) => {
        sigMap.set(row.routeId, `${toVehicleId(row)}:${row.assignedStops.map((s) => s.jobStopId).join(",")}`);
      });
      return sigMap;
    }, [workingRowData]);

    // Row data lookup by routeId for the polyline effect
    const workingRowByRouteId = useMemo(() => {
      if (!workingRowData) return null;
      const map = new Map<string, (typeof workingRowData)[number]>();
      workingRowData.forEach((row) => {
        map.set(row.routeId, row);
      });
      return map;
    }, [workingRowData]);

    const stopCoordinatesRef = useRef(stopCoordinates);
    stopCoordinatesRef.current = stopCoordinates;

    const workingRowByRouteIdRef = useRef(workingRowByRouteId);
    workingRowByRouteIdRef.current = workingRowByRouteId;

    // Seed the baseline from the optimizer's route.activities (the geometry route.points
    // draws) — NOT from routeSignatureMap/workingRowData. workingRowData may already exclude
    // jobs dismissed at load, so seeding from it would make those routes look unmodified and
    // never re-route (drawing the stale full polyline). Same `${id}:${stopIds}` format as
    // routeSignatureMap, so comparisons stay consistent.
    const initialRouteSignaturesRef = useRef<Map<string, string> | null | undefined>(undefined);
    if (initialRouteSignaturesRef.current === undefined && routes.length > 0 && routeSignatureMap !== null) {
      const sigs = new Map<string, string>();
      routes.forEach((route, i) => {
        // Dedupe jobStopIds (a Set, matching how generateGraphhopperAssignmentDetails builds
        // `sequences`). Self-paired stops — every delivery-only/pickup-only job — emit both a
        // pickup and a deliver activity at the same location_id, so the same jobStopId appears
        // twice here; without the dedup the baseline would never match the (deduped) row
        // signature and EVERY such route would look modified on load, firing getRouteWithEtas
        // on a clean plan.
        //
        // DP-3228: this is a two-sided invariant and it has silently broken once already.
        // edc219a95 (DP-3175) turned `sequences` into a plain array when the backend moved to
        // the Service model, where one stop yields one activity and duplicates are impossible —
        // leaving this comment's premise false. The backend now sends shipments again whenever
        // no existing manifest is selected, so duplicates are back and `sequences` is a Set
        // again. If you change either side, change both.
        const stopIds = [
          ...new Set(
            getStopActivities(route.activities)
              .map((activity) => extractJobAndJobStopIdsFromActivity(activity)[1])
              .filter((id): id is number => id != null)
          )
        ];
        sigs.set(`route-${i}`, `${route.vehicle_id ?? ""}:${stopIds.join(",")}`);
      });
      initialRouteSignaturesRef.current = sigs;
    }

    const routePathCacheRef = useRef(
      new Map<
        string,
        {
          path: {lat: number; lng: number}[];
          distance: number;
          etaByJobStopId?: Map<number, string>;
          etaSourceByJobStopId?: Map<number, string>;
          endDateTime?: string;
        }
      >()
    );

    // Stringified signature for stable effect dependency (changes when any route's driver/stops change)
    const routeSignatureKey = routeSignatureMap
      ? Array.from(routeSignatureMap.entries())
          .map(([k, v]) => `${k}=${v}`)
          .join("|")
      : null;

    // Draw polylines — use original geometry for unmodified routes, fetch only changed routes
    useEffect(() => {
      if (!map || !maps) return;

      let cancelled = false;

      // Clear existing polylines
      polylinesRef.current.forEach((entry) => entry.polyline.setMap(null));
      polylinesRef.current = [];

      const currentRowByRoute = workingRowByRouteIdRef.current;
      const currentStopCoordinates = stopCoordinatesRef.current;
      const initialSigs = initialRouteSignaturesRef.current;
      const currentSigs = routeSignatureMap;

      const bounds = new maps.LatLngBounds();
      let hasPoints = false;

      // Draw unmodified routes synchronously using original geometry
      const modifiedRouteIndices: number[] = [];
      routes.forEach((route, i) => {
        const routeId = `route-${i}`;
        const currentSig = currentSigs?.get(routeId);
        const initialSig = initialSigs?.get(routeId);

        // Route is deleted if routeId no longer exists in workingRowData
        if (currentSigs && !currentSigs.has(routeId)) return;

        const isThisRouteModified = currentSig != null && currentSig !== initialSig;

        if (isThisRouteModified) {
          modifiedRouteIndices.push(i);
          // Still include this route's stop coordinates in bounds so fitBounds covers
          // all routes, even those whose polyline will be async-fetched (DP-3142).
          const row = currentRowByRoute?.get(routeId);
          if (row) {
            row.assignedStops.forEach((stop) => {
              const coord = currentStopCoordinates?.get(stop.jobStopId);
              if (coord) {
                bounds.extend(new maps.LatLng(coord.lat, coord.lon));
                hasPoints = true;
              }
            });
          } else {
            getStopActivities(route.activities).forEach((activity) => {
              if (activity.address?.lat != null && activity.address?.lon != null) {
                bounds.extend(new maps.LatLng(activity.address.lat, activity.address.lon));
                hasPoints = true;
              }
            });
          }
          return;
        }

        if (!route.points || route.points.length === 0) {
          // DP-3175: Route has no polyline but may have stops (e.g. untouched
          // manifest rebuilt by backend). Queue for async fetch to get polyline.
          const row = currentRowByRoute?.get(routeId);
          if (row && row.assignedStops.length > 0) {
            modifiedRouteIndices.push(i);
            row.assignedStops.forEach((stop) => {
              const coord = currentStopCoordinates?.get(stop.jobStopId);
              if (coord) {
                bounds.extend(new maps.LatLng(coord.lat, coord.lon));
                hasPoints = true;
              }
            });
          }
          return;
        }

        // When showDriverReturnPath is false and the route has an end location,
        // skip the last segment (return from last stop to end location).
        // Routes planned without end location have no return segment.
        const endActivity = route.activities?.find((a) => a.type === "end");
        const hasEndLocation = endActivity?.address?.lat != null && endActivity?.address?.lon != null;
        const routePoints =
          !showDriverReturnPath && hasEndLocation && route.points.length > 1 ? route.points.slice(0, -1) : route.points;

        const path: {lat: number; lng: number}[] = [];
        routePoints.forEach((routePoint) => {
          if (!routePoint.coordinates) return;
          routePoint.coordinates.forEach((coord) => {
            if (coord.length >= 2) {
              const latLng = {lat: coord[1], lng: coord[0]};
              path.push(latLng);
              bounds.extend(new maps.LatLng(latLng.lat, latLng.lng));
              hasPoints = true;
            }
          });
        });

        if (path.length === 0) return;

        const row = currentRowByRoute?.get(routeId);
        const driverId = row?.driverId ?? 0;
        const isHidden = hiddenRouteIdsRef.current.includes(routeId);

        const polyline = new maps.Polyline({
          path,
          strokeColor: row?.routeColor ?? Constants.MAP.ROUTE_COLORS[i % Constants.MAP.ROUTE_COLORS.length],
          strokeWeight: 3,
          strokeOpacity: isHidden ? 0 : 1,
          map
        });
        polylinesRef.current.push({routeId, driverId, polyline});
      });

      if (hasPoints && !initialBoundsFittedRef.current) {
        map.fitBounds(bounds);
        initialBoundsFittedRef.current = true;
      }

      // Fetch modified routes asynchronously from GraphHopper
      if (modifiedRouteIndices.length > 0) {
        const fetchModifiedRoutes = async () => {
          const needsFetch = modifiedRouteIndices.some((i) => {
            const sig = currentSigs?.get(`route-${i}`);
            return sig != null && !routePathCacheRef.current.has(sig);
          });
          if (needsFetch) onRecalcLoadingChangeRef.current?.(true);
          await Promise.all(
            modifiedRouteIndices.map(async (i) => {
              if (cancelled) return;
              const routeId = `route-${i}`;
              const row = currentRowByRoute?.get(routeId);
              const currentSig = currentSigs?.get(routeId);
              if (!row || !currentSig) return;

              const drawPolyline = (path: {lat: number; lng: number}[]) => {
                const isHidden = hiddenRouteIdsRef.current.includes(row.routeId);
                const polyline = new maps.Polyline({
                  path,
                  strokeColor: row.routeColor ?? "#4B5563",
                  strokeWeight: 3,
                  strokeOpacity: isHidden ? 0 : 1,
                  map
                });
                polylinesRef.current.push({routeId: row.routeId, driverId: row.driverId!, polyline});
              };

              const cached = routePathCacheRef.current.get(currentSig);
              if (cached) {
                onRouteEtasUpdateRef.current?.(row.routeId, {
                  etaByJobStopId: cached.etaByJobStopId ?? new Map(),
                  etaSourceByJobStopId: cached.etaSourceByJobStopId ?? new Map(),
                  endDateTime: cached.endDateTime,
                  miles: cached.distance
                });
                drawPolyline(cached.path);
                return;
              }

              // DP-3099 B02: Route has no assigned stops — clear miles/path, don't draw
              if (row.assignedStops.length === 0) {
                onRouteEtasUpdateRef.current?.(row.routeId, {
                  etaByJobStopId: new Map(),
                  etaSourceByJobStopId: new Map(),
                  endDateTime: undefined,
                  miles: 0
                  // DP-3147: an emptied route drives nowhere — 0 here is a real value, not
                  // "unknown", so it must be explicit rather than relying on the keep-previous
                  // fallback in handleRouteEtasUpdate.
                });
                return;
              }

              const routeWithEtasVariables = buildRouteWithEtasVariables(
                row,
                currentStopCoordinates,
                dwellSecondsRef.current ?? 0
              );
              if (!routeWithEtasVariables) return;

              try {
                const response = await getRouteWithEtasRef.current({
                  variables: routeWithEtasVariables
                });
                if (cancelled) return;

                const parsed = parseRouteWithEtasResult(
                  response.data?.getRouteWithEtas?.items?.[0],
                  dwellSecondsRef.current ?? 0
                );
                if (!parsed) return;

                // DP-3219: Always apply miles/ETAs even when polyline is empty —
                // the cost calculator needs miles/duration from getRouteWithEtas.
                onRouteEtasUpdateRef.current?.(row.routeId, {
                  etaByJobStopId: parsed.etaByJobStopId,
                  etaSourceByJobStopId: parsed.etaSourceByJobStopId,
                  endDateTime: parsed.endDateTime,
                  miles: parsed.miles
                });

                if (parsed.path.length === 0) return;

                routePathCacheRef.current.set(currentSig, {
                  path: parsed.path,
                  distance: parsed.miles,
                  etaByJobStopId: parsed.etaByJobStopId,
                  etaSourceByJobStopId: parsed.etaSourceByJobStopId,
                  endDateTime: parsed.endDateTime
                });
                drawPolyline(parsed.path);
              } catch (err) {
                log.warn("[RoutesResultMap] getRouteWithEtas failed for driver", row.driverId, err);
              }
            })
          );
          if (!cancelled) onRecalcLoadingChangeRef.current?.(false);
        };
        fetchModifiedRoutes();
      }

      return () => {
        cancelled = true;
        onRecalcLoadingChangeRef.current?.(false);
        polylinesRef.current.forEach((entry) => entry.polyline.setMap(null));
        polylinesRef.current = [];
      };
      // Only re-run when map initializes, original routes load, route structure changes,
      // or new stop coordinates become available (DP-3219: geocoded manifest stops)
      // routeSignatureKey changes on driver/stop/order changes but NOT on visibility toggles
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, maps, routes, routeSignatureKey, stopCoordinates]);

    // Update polyline opacity when hidden route list changes
    useEffect(() => {
      polylinesRef.current.forEach((entry) => {
        entry.polyline.setOptions({strokeOpacity: hiddenRouteIds.includes(entry.routeId) ? 0 : 1});
      });
    }, [hiddenRouteIds]);

    // Toggle traffic layer
    useEffect(() => {
      if (!map || !maps) return;
      if (isTrafficActive) {
        if (!trafficLayerRef.current) {
          trafficLayerRef.current = new maps.TrafficLayer();
        }
        trafficLayerRef.current.setMap(map);
      } else {
        trafficLayerRef.current?.setMap(null);
      }
    }, [isTrafficActive, map, maps]);

    // Change map type
    useEffect(() => {
      if (!map) return;
      map.setMapTypeId(mapType);
    }, [mapType, map]);

    const handleTrafficClick = useCallback(() => onTrafficToggle(), [onTrafficToggle]);
    const handleChangeMapType = useCallback((type: string) => onMapTypeChange(type), [onMapTypeChange]);

    const stopMarkers = useMemo(() => {
      // Use workingRowData for stop markers when available (reflects modifications)
      if (workingRowData) {
        return workingRowData.flatMap((row) => {
          const color = row.routeColor ?? "#4B5563";
          return row.assignedStops.flatMap((agStop) => {
            // DP-3175: Do not draw completed stops on the map
            if (agStop.jobStopStatus === "C") return [];
            const coord = stopCoordinates.get(agStop.jobStopId);
            if (!coord) return [];
            const job = agStop.job;
            if (!job) return [];
            const info: StopInfoMetadata = {
              key: `${job.jobId}:${agStop.jobStopId}`,
              jobNumber: job.jobNumber,
              customer: agStop.order?.customer?.name ?? "",
              service: job.service ?? (agStop as any).order?.service ?? "",
              visibilityLevel: EntityVisibilityLevel.MARKER,
              routeColor: color,
              isFirstStop: false,
              isRoutePlanning: true
            };
            return [
              {
                lat: coord.lat,
                lng: coord.lon,
                routeId: row.routeId,
                driverId: row.driverId!,
                stop: {...agStop, manifestSequence: agStop.manifestSequence ?? 0},
                info,
                color
              }
            ];
          });
        });
      }

      // Fallback to original routes
      return routes.flatMap((route, routeIndex) => {
        const driverId = 0; // fallback markers; real driverId comes from workingRowData
        const color = Constants.MAP.ROUTE_COLORS[routeIndex % Constants.MAP.ROUTE_COLORS.length];
        const stopActivities = getStopActivities(route.activities);
        // DP-3228: the fourth consequence listed in RoutePlanning.utils.ts reaches here too.
        // getStopActivities de-duplicates on location_id + type, so under the shipment model
        // one stop arrives as TWO activities — every self-paired job, and every Both stop.
        // The branch above is safe because assignedStops comes from the de-duplicated
        // `sequences`; this one reads route.activities directly, so it needs its own guard or
        // it emits two markers with the same React key and shifts the visit number of every
        // stop after it. Reachable on the first render after a plan returns, before the
        // effect fills workingRowData. Counting emitted markers rather than activity index
        // also closes a pre-existing gap: an activity dropped below for missing coordinates
        // or an unresolvable job used to leave a hole in the numbering.
        const seenStopIds = new Set<number>();

        return stopActivities.flatMap((activity) => {
          const lat = activity.address?.lat;
          const lon = activity.address?.lon;
          if (lat == null || lon == null || !activity.location_id) return [];
          const [jobId, jobStopId] = extractJobAndJobStopIdsFromActivity(activity);
          if (!jobId || !jobStopId) return [];
          const job = selectedJobs.find((j) => j.jobId === jobId);
          const stop = job?.stops.find((s) => s.jobStopId === jobStopId);
          if (!job || !stop) return [];
          if (seenStopIds.has(jobStopId)) return [];
          seenStopIds.add(jobStopId);
          const stopWithSequence = {...stop, manifestSequence: seenStopIds.size};
          const info: StopInfoMetadata = {
            key: activity.location_id,
            jobNumber: job.jobNumber,
            customer: job.order.customer?.name ?? "",
            service: job.service ?? "",
            visibilityLevel: EntityVisibilityLevel.MARKER,
            routeColor: color,
            isFirstStop: false,
            isRoutePlanning: true
          };
          return [{lat, lng: lon, routeId: `route-${routeIndex}`, driverId, stop: stopWithSequence, info, color}];
        });
      });
    }, [routes, selectedJobs, workingRowData, stopCoordinates]);

    const visibleStopMarkers = useMemo(
      () => stopMarkers.filter((m) => !hiddenRouteIds.includes(m.routeId)),
      [stopMarkers, hiddenRouteIds]
    );

    const stopClusters = useMemo(() => {
      const groups = new Map<string, (typeof stopMarkers)[number][]>();
      visibleStopMarkers.forEach((marker) => {
        const key = `${marker.lat},${marker.lng}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(marker);
      });
      return groups;
    }, [visibleStopMarkers]);

    const driverMarkers = useMemo(() => {
      // Use workingRowData for driver markers when available (reflects driver changes)
      if (workingRowData) {
        return workingRowData.flatMap((row) => {
          const color = row.routeColor ?? "#4B5563";
          const lat = row.startLat;
          const lng = row.startLng;
          if (lat == null || lng == null) return [];
          const displayLabel = isShowCode ? row.driverCode ?? "" : row.name ?? row.driverCode ?? "";
          return [
            {
              lat,
              lng,
              routeId: row.routeId,
              driverId: row.driverId!,
              displayLabel,
              vehicleIcon: VehicleIconResolver(row.vehicleDescription, tenantPreferences),
              color
            }
          ];
        });
      }

      return routes.flatMap((route, routeIndex) => {
        const color = Constants.MAP.ROUTE_COLORS[routeIndex % Constants.MAP.ROUTE_COLORS.length];
        const startActivity = route.activities?.find((a) => a.type === "start");
        const lat = startActivity?.address?.lat;
        const lon = startActivity?.address?.lon;
        if (lat == null || lon == null) return [];
        const driver = findDriverByVehicleId(route.vehicle_id, selectedDrivers);
        if (!driver) return [];
        const driverId = driver.driverId ?? 0;
        const displayLabel = isShowCode ? driver.driverCode ?? "" : driver.name ?? driver.driverCode ?? "";
        return [
          {
            lat,
            lng: lon,
            routeId: `route-${routeIndex}`,
            driverId,
            displayLabel,
            vehicleIcon: VehicleIconResolver(driver.vehicleDescription, tenantPreferences),
            color
          }
        ];
      });
    }, [routes, selectedDrivers, tenantPreferences, isShowCode, workingRowData]);

    const visibleDriverMarkers = useMemo(
      () => driverMarkers.filter((m) => !hiddenRouteIds.includes(m.routeId)),
      [driverMarkers, hiddenRouteIds]
    );

    // Group driver markers by location so overlapping drivers cluster together.
    // Round to 5 decimal places (~1m precision) to handle floating point differences
    // between optimizer coordinates and geocoded coordinates.
    const driverClusters = useMemo(() => {
      const groups = new Map<string, typeof driverMarkers>();
      visibleDriverMarkers.forEach((marker) => {
        const key = `${marker.lat.toFixed(5)},${marker.lng.toFixed(5)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(marker);
      });
      return groups;
    }, [visibleDriverMarkers]);

    // Whether all routes are currently hidden or no routes exist
    const noRoutes = workingRowData != null && workingRowData.length === 0;
    const allHidden = useMemo(() => {
      if (noRoutes) return true;
      const allRouteIds = (workingRowData ?? []).map((r) => r.routeId);
      if (allRouteIds.length === 0) {
        return routes.length > 0 && routes.every((_, i) => hiddenRouteIds.includes(`route-${i}`));
      }
      return allRouteIds.every((id) => hiddenRouteIds.includes(id));
    }, [routes, hiddenRouteIds, workingRowData, noRoutes]);

    // Coordinates for MapReframe (only visible drivers/stops/routes)
    const driverCords = useMemo(
      () => visibleDriverMarkers.map((m) => ({lat: m.lat, lng: m.lng})),
      [visibleDriverMarkers]
    );

    const stopCords = useMemo(() => visibleStopMarkers.map((m) => ({lat: m.lat, lng: m.lng})), [visibleStopMarkers]);

    const routeCords = useMemo(() => {
      if (workingRowData) {
        return workingRowData
          .filter((row) => !hiddenRouteIds.includes(row.routeId))
          .flatMap((row) =>
            row.assignedStops.flatMap((stop) => {
              const coord = stopCoordinates.get(stop.jobStopId);
              return coord ? [{lat: coord.lat, lng: coord.lon}] : [];
            })
          );
      }
      return routes
        .filter((_, i) => !hiddenRouteIds.includes(`route-${i}`))
        .flatMap((route) =>
          (route.points ?? []).flatMap((rp) =>
            (rp.coordinates ?? []).flatMap((coord) => (coord.length >= 2 ? [{lat: coord[1], lng: coord[0]}] : []))
          )
        );
    }, [routes, hiddenRouteIds, workingRowData, stopCoordinates]);

    return (
      <MapContainer ref={mapContainerRef}>
        <GoogleMapReact
          bootstrapURLKeys={{key: Constants.GOOGLE_API_KEY}}
          defaultCenter={Constants.MAP.DEFAULT_CENTER}
          defaultZoom={Constants.MAP.DEFAULT_ZOOM}
          yesIWantToUseGoogleMapApiInternals
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            clickableIcons: true,
            rotateControl: false,
            fullscreenControl: false
          }}
          onGoogleApiLoaded={apiIsLoaded}
        >
          {Array.from(driverClusters.entries()).flatMap(([locationKey, markers]) => {
            if (markers.length === 1) {
              const marker = markers[0];
              return [
                <DriverPin
                  key={`driver-${marker.driverId}-${marker.color}`}
                  lat={marker.lat}
                  lng={marker.lng}
                  driverCode={marker.displayLabel}
                  vehicleIcon={marker.vehicleIcon}
                  color={marker.color}
                  newWindow={newWindow}
                  usePortal={usePortal}
                />
              ];
            }
            return [
              <ClusteredDriverPins
                key={`driver-cluster-${locationKey}`}
                lat={markers[0].lat}
                lng={markers[0].lng}
                markers={markers}
                newWindow={newWindow}
                usePortal={usePortal}
              />
            ];
          })}
          {Array.from(stopClusters.entries()).flatMap(([key, markers]) => {
            if (markers.length === 1) {
              const marker = markers[0];
              return [
                <StopPin
                  key={marker.info.key}
                  lat={marker.lat}
                  lng={marker.lng}
                  stop={marker.stop}
                  info={marker.info}
                  color={marker.color}
                  newWindow={newWindow}
                  usePortal={usePortal}
                />
              ];
            }
            return [
              <ClusteredStopsPin
                key={key}
                lat={markers[0].lat}
                lng={markers[0].lng}
                markers={markers}
                newWindow={newWindow}
                usePortal={usePortal}
              />
            ];
          })}
        </GoogleMapReact>
        <RoutePlanningActionBar
          mapResponsiveWidth={mapContainerWidth}
          mapType={mapType}
          isTrafficActive={isTrafficActive}
          allHidden={allHidden}
          noRoutes={noRoutes}
          onTrafficClicked={handleTrafficClick}
          onChangeMapType={handleChangeMapType}
          onToggleAll={onToggleAll}
          newWindow={newWindow}
          usePortal={usePortal}
        />
        <MapReframe
          map={map}
          maps={maps}
          driverCords={driverCords}
          jobCords={stopCords}
          stopCords={[]}
          routeCords={routeCords}
          newWindow={newWindow}
          usePortal={usePortal}
          isPortalOpen={isPortalOpen}
          onPopOut={onPopOut}
        />
      </MapContainer>
    );
  }
);

RoutesResultMap.displayName = "RoutesResultMap";

export default RoutesResultMap;

const MapContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;
